import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { aboutTimeline, siteStats, teamMembers } from "@/mocks/site";

const serviceScope = [
  { icon: "ri-restaurant-2-line", title: "美食外卖配送", desc: "覆盖全城中餐、西餐、甜点与饮品，热食冷链保温箱配送，平均 30 分钟送达。" },
  { icon: "ri-e-bike-2-line", title: "同城急送", desc: "生鲜、药品、文件、数码产品均可寄送，就近派单，最快 30 分钟上门取件。" },
  { icon: "ri-snowy-line", title: "冷链生鲜配送", desc: "全程冷链温控，鲜活水产、冷鲜肉类、冰淇淋等特殊品类也能稳妥送达。" },
  { icon: "ri-building-2-line", title: "企业团餐与批量配送", desc: "为写字楼、学校、企业提供团餐与下午茶批量配送，支持开发票与专属客服。" },
];

export default function About() {
  usePageMeta(
    "关于我们 - 美味的快递 | 品牌故事与服务理念",
    "了解美味的快递的品牌故事、服务范围、团队风采与发展历程，我们致力于用最快的速度，把美味与心意送到每一位用户手中。",
    "美味的快递,关于我们,外卖平台,快递服务,品牌故事,团队介绍"
  );

  return (
    <div className="bg-background-50">
      <section className="relative h-[340px] w-full overflow-hidden md:h-[440px]">
        <img
          src="https://readdy.ai/api/search-image?query=warm%20modern%20restaurant%20team%20welcoming%20customers%2C%20bright%20inviting%20interior%20with%20wood%20and%20cream%20tones%2C%20golden%20afternoon%20light%2C%20editorial%20documentary%20photography%2C%20warm%20orange%20accents%2C%20high%20detail&width=1600&height=900&seq=about-hero&orientation=landscape"
          alt="美味的快递品牌团队"
          title="美味的快递 - 品牌与服务理念"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/60" />
        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-4 py-1.5 text-xs font-bold text-background-50">
              <i className="ri-store-2-line" />
              关于美味的快递
            </span>
            <h1 className="mt-5 max-w-2xl font-heading text-3xl font-extrabold leading-tight text-white md:text-5xl">
              让美味与心意，以最快的速度抵达
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/85 md:text-base">
              从一间 30 平米的小厨房，到覆盖全城的配送网络，我们始终相信：好吃的食物，值得被认真对待。
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 pb-14 md:px-8 md:pb-20">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-14">
            <div className="h-[300px] w-full overflow-hidden rounded-lg md:h-[420px]">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20cozy%20small%20restaurant%20kitchen%20with%20chef%20carefully%20plating%20a%20dish%2C%20steam%20rising%2C%20amber%20and%20orange%20tones%2C%20soft%20golden%20light%2C%20editorial%20documentary%20photography%2C%20authentic%20and%20inviting%2C%20high%20detail&width=1000&height=1100&seq=about-story&orientation=portrait"
                alt="美味的快递品牌厨房"
                title="美味的快递 - 品牌故事"
                className="h-full w-full object-top"
              />
            </div>
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3.5 py-1.5 text-xs font-bold text-primary-700">
                <i className="ri-book-open-line" />
                品牌故事
              </span>
              <h2 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                从一份外卖，到一座城市的速度
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                「美味的快递」成立于 2019 年，最初只是三个热爱美食的年轻人经营的一间小厨房。为了让更多人吃到刚出锅的味道，我们自己做配送、自己设计保温箱，一步步把「快」变成了品牌最被信任的标签。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                如今，我们已连接 800 多家严选商家，自建覆盖 120+ 片区的配送网络，把美食外卖与同城快递合二为一，让每一次期待都能准时抵达。
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4">
                {siteStats.map((s) => (
                  <div key={s.label} className="rounded-lg border border-background-200 bg-background-100 p-4">
                    <p className="font-heading text-xl font-extrabold text-primary-600">{s.value}</p>
                    <p className="mt-1 text-xs text-foreground-500">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-8 md:py-20">
          <div className="flex flex-col items-center text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3.5 py-1.5 text-xs font-bold text-accent-700">
              <i className="ri-service-line" />
              服务范围
            </span>
            <h2 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              一站式美食与快递服务
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-foreground-500 md:text-base">
              从一顿热饭到一个急件，我们都能帮你稳妥地送达。
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {serviceScope.map((s) => (
              <div key={s.title} className="rounded-lg border border-background-200 bg-background-50 p-6 transition hover:border-primary-300">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className={`${s.icon} text-2xl`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-8 md:py-20">
        <div className="flex flex-col items-center text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3.5 py-1.5 text-xs font-bold text-secondary-900">
            <i className="ri-team-line" />
            团队风采
          </span>
          <h2 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            一群认真对待每份食物的人
          </h2>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-5 lg:grid-cols-4">
          {teamMembers.map((m) => (
            <article key={m.name} className="flex flex-col items-center rounded-lg border border-background-200 bg-background-100 p-5 text-center">
              <img
                src={m.avatar}
                alt={`${m.name} - ${m.role}`}
                title={`${m.name} - 美味的快递${m.role}`}
                className="h-24 w-24 rounded-full object-cover"
              />
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{m.name}</h3>
              <p className="mt-1 text-xs text-foreground-500">{m.role}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-8 md:py-20">
          <div className="flex flex-col items-center text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3.5 py-1.5 text-xs font-bold text-primary-700">
              <i className="ri-route-line" />
              发展历程
            </span>
            <h2 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              一步一个脚印，走到你的餐桌旁
            </h2>
          </div>

          <div className="relative mt-12">
            <span className="absolute left-4 top-0 h-full w-px bg-background-300 lg:left-1/2" />
            <div className="flex flex-col gap-8">
              {aboutTimeline.map((item, i) => (
                <div
                  key={item.year}
                  className={`relative flex flex-col gap-3 pl-12 lg:w-1/2 lg:pl-0 ${
                    i % 2 === 0 ? "lg:pr-14 lg:text-right" : "lg:ml-auto lg:pl-14"
                  }`}
                >
                  <span
                    className={`absolute left-4 top-1.5 flex h-4 w-4 -translate-x-1/2 items-center justify-center rounded-full bg-primary-500 ring-4 ring-background-100 lg:left-auto ${
                      i % 2 === 0 ? "lg:right-0 lg:translate-x-1/2" : "lg:left-0 lg:-translate-x-1/2"
                    }`}
                  />
                  <span className="font-heading text-2xl font-extrabold text-primary-600">{item.year}</span>
                  <h3 className="font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                  <p className="text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-8 md:py-20">
        <div className="flex flex-col items-center gap-6 rounded-lg border border-background-200 bg-primary-50 px-6 py-12 text-center">
          <h2 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">想和我们一起，把美味送得更快？</h2>
          <p className="max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            无论你是想加盟合作、商家入驻，还是加入配送团队，都欢迎随时联系我们。
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3.5 text-sm font-bold text-background-50 transition hover:bg-primary-600"
            >
              <i className="ri-mail-send-line" />
              联系我们
            </Link>
            <Link
              to="/food-category"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-primary-300 bg-background-50 px-7 py-3.5 text-sm font-bold text-primary-700 transition hover:bg-primary-100"
            >
              <i className="ri-restaurant-2-line" />
              去点一份美食
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}